import { useState } from 'react';
import { Link } from 'react-router';
import { UilLock } from '@iconscout/react-unicons';
import { Button } from '../buttons/Button';
import { PlayLectureCard } from '../cards/PlayLectureCard';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import API from '../../lib/api';
import { Lecture } from '../../lib/types';
import {
    PlanWeek as PlanWeekType,
    themeDimensionLabel,
} from '../../lib/plan/types';

interface PlanWeekProps {
    week: PlanWeekType;
    // Proč je týden zamčený: 'purchase' = demo licence (nabídneme koupi),
    // 'progress' = zaplaceno, ale předchozí týden ještě není dohraný.
    lockReason?: 'purchase' | 'progress';
    showUpsell?: boolean;
    onMutate: () => void;
}

export function PlanWeek({
    week,
    lockReason = 'purchase',
    showUpsell = false,
    onMutate,
}: PlanWeekProps) {
    // Slug čekající na potvrzení dokončení — pojistka proti omylu z přehrání
    // „na zkoušku": dohrání videa ani „Hotovo" plán neposune bez potvrzení.
    const [confirmSlug, setConfirmSlug] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    const closeConfirm = () => {
        setConfirmSlug(null);
        setFailed(false);
    };

    const confirmComplete = () => {
        const slug = confirmSlug;
        if (!slug) return;
        setFailed(false);
        // Posun řeší plán, ne lekce — chybějící lekce nesmí uživatele zaseknout.
        API.post('/plan/advance', { exercise_id: slug })
            .then(() => {
                closeConfirm();
                onMutate();
            })
            .catch(() => setFailed(true));
    };

    const heading = <h4 className="mb-4">Týden {week.week}</h4>;

    if (week.locked) {
        return (
            <div>
                {heading}
                <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cloud-100 text-ink-800">
                        <UilLock size={20} />
                    </div>
                    <div className="opacity-70">
                        <p className="text-ink-800">
                            {themeDimensionLabel(week.theme_key)}
                        </p>
                        {lockReason === 'progress' && (
                            <p className="text-sm text-ink-700">
                                Odemkne se po dokončení předchozího týdne.
                            </p>
                        )}
                    </div>
                </div>

                {lockReason === 'purchase' && showUpsell && (
                    <div className="mt-4 rounded-2xl bg-white p-6 text-center shadow-sm">
                        <p className="mb-4 text-ink-800">
                            Odemkněte všechny týdny a projděte si celý plán bez omezení.
                        </p>
                        <Link to="/koupit" className="inline-block">
                            <Button color="product">Odemkněte celý rok</Button>
                        </Link>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {heading}
            <div className="grid gap-6">
                {week.days.map((day) => {
                    const lectureProp: Lecture = {
                        name: day.lecture?.name ?? day.exercise_id,
                        methodology: day.lecture?.methodology ?? null,
                        video: day.lecture?.video ?? {
                            url: '',
                            poster: '',
                            length: 0,
                        },
                        // poziční zámek: budoucí den v aktuálním týdnu jen název
                        is_locked: !(day.is_completed || day.is_current),
                        user_completed: day.is_completed,
                        slug: day.exercise_id,
                        // nepoužito kartou; jen musí projít typovou kontrolou
                        category: { name: '', slug: '', color: 'turquoise' },
                    };

                    return (
                        <PlayLectureCard
                            key={day.exercise_id}
                            lecture={lectureProp}
                            color="product"
                            isActive={day.is_current}
                            onLessonVideoStart={() => {
                                API.post(
                                    `/lectures/${day.exercise_id}/started`
                                );
                            }}
                            onLessonEnd={() => setConfirmSlug(day.exercise_id)}
                        />
                    );
                })}
            </div>

            <ConfirmDialog
                isOpen={confirmSlug !== null}
                onClose={closeConfirm}
                onConfirm={confirmComplete}
                heading={failed ? 'Uložení se nezdařilo' : 'Dokončit cvičení?'}
                message={
                    failed
                        ? 'Cvičení se nepodařilo označit jako hotové. Zkuste to prosím znovu.'
                        : 'Označíme cvičení jako hotové a plán se posune na další den.'
                }
                confirmLabel={failed ? 'Zkusit znovu' : 'Ano, hotové'}
                cancelLabel="Zpět"
            />
        </div>
    );
}
